package diana.soleil.design.iraniancanadienbazzar.core.firebase

/**
 * Typed snapshot of the Remote Config keys deployed in `firebase/remoteconfig.template.json`.
 * Adding a key here means adding it to that template + `scripts/firebase-deploy.sh remoteconfig`
 * (golden rule #8). Defaults mirror the template so the app behaves sanely before the first fetch.
 */
data class RemoteConfigValues(
    val minSupportedAppVersion: String,
    val chatEnabled: Boolean,
    val feedEnabled: Boolean,
    val marketplaceEnabled: Boolean,
    val orderingEnabled: Boolean,
    val eventsEnabled: Boolean,
    /** Hidden Gems hub kill switch (`feature_hiddengems_enabled`, default true). */
    val hiddenGemsEnabled: Boolean,
    /** Danestaniha section kill switch (`feature_danestaniha_enabled`, default true). */
    val danestanihaEnabled: Boolean,
    /** Shahnameh tales section kill switch (`feature_shahnameh_enabled`, default true). */
    val shahnamehEnabled: Boolean,
    /** Persian poetry section kill switch (`feature_poetry_enabled`, default true). */
    val poetryEnabled: Boolean,
    /** Short-stories section kill switch (`feature_stories_enabled`, default true). */
    val storiesEnabled: Boolean,
    /** `feature_fal_enabled` — Fal-e Hafez inside Hidden Gems. */
    val falEnabled: Boolean,
    /** Merchant deals/promotions kill switch (`feature_deals_enabled`, default true). */
    val dealsEnabled: Boolean,
    /** Find-your-people discovery kill switch (`feature_people_enabled`, default true). */
    val peopleEnabled: Boolean,
    /**
     * Stripe paid surfaces (`feature_payments_enabled`, default **FALSE**). Gates the dashboard
     * "Promote listing" sheet and Profile "Mahaleh Pro". Stripe is test-mode only; leaving this off
     * is docs/GO_LIVE.md Option A (launch free, payments as a fast-follow).
     */
    val paymentsEnabled: Boolean,
    /**
     * Home density (`home_dense_sections_enabled`, default **FALSE** = launch mode).
     *
     * Home can render eight rails (Categories, Featured, For You, People, Deals, News, Gems,
     * Popular). At launch density most of those resolve thin or empty, which reads as an empty app
     * rather than a rich one, so the discretionary rails (For You / People / Deals / News) stay off
     * until there is content to fill them. Flip to true — no store release — once a metro has real
     * volume. See `docs/PROGRESS.md` 2026-08-02.
     */
    val homeDenseSectionsEnabled: Boolean,
    /** Adult jokes tier (`gems_adult_jokes_enabled`, default FALSE — store-rating decision). */
    val gemsAdultJokesEnabled: Boolean,
    val sponsoredSlotsMax: Int,
    /** Metro ids live for rollout (gta|mtl|yvr); see `docs/LOCALIZATION.md`. */
    val activeLaunchMetros: List<String>,
    /** Base URL for share links (dev overrides to the dev Hosting site). */
    val shareBaseUrl: String,
    /** Wind-down mode: shows an app-wide banner and (Phase 6+) blocks new charges without a release. */
    val sunsetModeEnabled: Boolean,
    /** Localized wind-down message; resolved by active language. Empty = fall back to a default string. */
    val sunsetMessageEn: String,
    val sunsetMessageFr: String,
    val sunsetMessageFa: String,
    /** Kill-switch: block the whole app behind a maintenance screen (backend down/migrating). */
    val maintenanceModeEnabled: Boolean,
    /** Localized maintenance message; empty = fall back to a bundled default string. */
    val maintenanceMessageEn: String,
    val maintenanceMessageFr: String,
    val maintenanceMessageFa: String,
    /** Where the force-update button sends users (store listing once live; web app until then). */
    val forceUpdateUrl: String,
) {
    fun isMetroActive(metroId: String): Boolean =
        activeLaunchMetros.any { it.equals(metroId, ignoreCase = true) }

    companion object {
        /** Defaults identical to the Remote Config template, used before/if a fetch hasn't completed. */
        val Defaults = RemoteConfigValues(
            minSupportedAppVersion = "1.0.0",
            chatEnabled = true,
            feedEnabled = true,
            marketplaceEnabled = true,
            orderingEnabled = true,
            eventsEnabled = true,
            hiddenGemsEnabled = true,
            danestanihaEnabled = true,
            shahnamehEnabled = true,
            poetryEnabled = true,
            storiesEnabled = true,
            falEnabled = true,
            dealsEnabled = true,
            peopleEnabled = true,
            paymentsEnabled = false,
            homeDenseSectionsEnabled = false,
            gemsAdultJokesEnabled = false,
            sponsoredSlotsMax = 3,
            activeLaunchMetros = listOf("gta", "mtl", "yvr"),
            shareBaseUrl = "https://mahaleh.ca",
            sunsetModeEnabled = false,
            sunsetMessageEn = "",
            sunsetMessageFr = "",
            sunsetMessageFa = "",
            maintenanceModeEnabled = false,
            maintenanceMessageEn = "",
            maintenanceMessageFr = "",
            maintenanceMessageFa = "",
            forceUpdateUrl = "https://mahaleh.ca/app/",
        )
    }
}

/** Remote Config key names — single source so the wrapper and any tooling agree. */
internal object RemoteConfigKeys {
    const val MIN_SUPPORTED_APP_VERSION = "min_supported_app_version"
    const val FEATURE_CHAT = "feature_chat_enabled"
    const val FEATURE_FEED = "feature_feed_enabled"
    const val FEATURE_MARKETPLACE = "feature_marketplace_enabled"
    const val FEATURE_ORDERING = "feature_ordering_enabled"
    const val FEATURE_EVENTS = "feature_events_enabled"
    const val FEATURE_HIDDEN_GEMS = "feature_hiddengems_enabled"
    const val FEATURE_DANESTANIHA = "feature_danestaniha_enabled"
    const val FEATURE_SHAHNAMEH = "feature_shahnameh_enabled"
    const val FEATURE_POETRY = "feature_poetry_enabled"
    const val FEATURE_STORIES = "feature_stories_enabled"
    const val FEATURE_FAL = "feature_fal_enabled"
    const val FEATURE_DEALS = "feature_deals_enabled"
    const val FEATURE_PEOPLE = "feature_people_enabled"
    const val FEATURE_PAYMENTS = "feature_payments_enabled"
    const val HOME_DENSE_SECTIONS = "home_dense_sections_enabled"
    const val GEMS_ADULT_JOKES = "gems_adult_jokes_enabled"
    const val SPONSORED_SLOTS_MAX = "sponsored_slots_max"
    const val ACTIVE_LAUNCH_CITIES = "active_launch_cities"
    const val SHARE_BASE_URL = "share_base_url"
    const val SUNSET_MODE_ENABLED = "sunset_mode_enabled"
    const val SUNSET_MESSAGE_EN = "sunset_message_en"
    const val SUNSET_MESSAGE_FR = "sunset_message_fr"
    const val SUNSET_MESSAGE_FA = "sunset_message_fa"
    const val MAINTENANCE_MODE_ENABLED = "maintenance_mode_enabled"
    const val MAINTENANCE_MESSAGE_EN = "maintenance_message_en"
    const val MAINTENANCE_MESSAGE_FR = "maintenance_message_fr"
    const val MAINTENANCE_MESSAGE_FA = "maintenance_message_fa"
    const val FORCE_UPDATE_URL = "force_update_url"
}

internal fun parseMetros(raw: String): List<String> =
    raw.split(",").map { it.trim() }.filter { it.isNotEmpty() }
